import { useEffect, useRef, useState } from "react";
import { projectsApi } from "@/api/mist";

// Crash-protected project-name field: a hybrid text input + autocomplete of the
// user's active/open projects. Degrades to a plain input if the list can't load
// (never throws), preserving the caller's value/onChange/testid contract.
export default function ProjectNameCombobox({ value, onChange, testid, placeholder, className, maxLength = 80 }) {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await projectsApi.list();
        if (!dead) setProjects(Array.isArray(r?.projects) ? r.projects : []);
      } catch { /* offline / no projects — stay a plain input */ }
    })();
    return () => { dead = true; };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = (value || "").trim().toLowerCase();
  const opts = projects
    .map((p) => (p?.title || p?.name || "").trim())
    .filter((t) => t && (!q || t.toLowerCase().includes(q)))
    .filter((t, i, a) => a.indexOf(t) === i)
    .slice(0, 8);

  return (
    <div ref={boxRef} className="relative">
      <input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} data-testid={testid} placeholder={placeholder} maxLength={maxLength}
        className={className} autoComplete="off" role="combobox" aria-expanded={open} />
      {open && opts.length > 0 && (
        <div data-testid={`${testid}-menu`} className="absolute left-0 right-0 top-full z-[60] mt-1 max-h-52 overflow-y-auto border-2 border-[#00F5D4] bg-[#0d0d0d] shadow-[4px_4px_0px_#000]">
          {opts.map((t) => (
            <button key={t} type="button" data-testid={`${testid}-opt`}
              onMouseDown={(e) => { e.preventDefault(); onChange(t); setOpen(false); }}
              className="block w-full truncate px-3 py-2 text-left font-mono text-[12px] text-white hover:bg-[#00F5D4]/15">{t}</button>
          ))}
        </div>
      )}
    </div>
  );
}
